/**
 * ChartTheme — token-driven palette and typography defaults for the
 * Recharts-based chart primitives in @csk/demo-ui.
 *
 * Every chart in the demo library sources color, grid, and axis tone
 * decisions from this module so the chart layer respects
 * `tokens.data.json` rather than hard-coding hex literals. Callers may
 * override individual fields, but the default surface keeps the visual
 * identity aligned across SecuritySurfacePanel, AttackSurfaceConvergence,
 * DifferentialSecurity, and Governed-K panels.
 *
 * Theorem-keyed lookups resolve directly against `THEOREM_COLOR` so a
 * renamed theorem or a recoloured visual-identity entry propagates here
 * without a code change.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  FONT,
  GRAY,
  PRIMARY,
  SAFE,
  THEOREM_COLOR,
  WARN,
  type CanonicalTheoremName,
} from "../../design-system/tokens.js";

/**
 * Token-aligned chart palette.
 *
 * The fields are intentionally narrow — a chart primitive consumes only
 * what it needs (axis tone, grid tone, primary series color, etc.) and
 * never pulls raw token literals at the call site.
 */
export interface ChartPalette {
  /** Axis tick + label text color. */
  readonly axis: string;
  /** Axis line color. */
  readonly axisLine: string;
  /** Cartesian grid color. */
  readonly grid: string;
  /** Primary series color. */
  readonly primary: string;
  /** Secondary series color. */
  readonly secondary: string;
  /** Safe-tone accent (used for `neutralized` series). */
  readonly safe: string;
  /** Warn-tone accent (used for vulnerable / failed series). */
  readonly warn: string;
  /** Tooltip background. */
  readonly tooltipBg: string;
  /** Tooltip border. */
  readonly tooltipBorder: string;
  /** Tooltip text color. */
  readonly tooltipText: string;
}

/** Default token-aligned chart palette. */
export const DEFAULT_CHART_PALETTE: ChartPalette = {
  axis: GRAY[700],
  axisLine: GRAY[400],
  grid: GRAY[300],
  primary: PRIMARY[700],
  secondary: PRIMARY[500],
  safe: SAFE[700],
  warn: WARN[700],
  // `GRAY[50]` resolves to white in tokens.data.json. Routing the
  // tooltip background through the token scale keeps the palette
  // entirely token-driven so a future tokens.data.json recolour
  // propagates here without a code change.
  tooltipBg: GRAY[50],
  tooltipBorder: GRAY[300],
  tooltipText: GRAY[900],
} as const;

/** Shared typography defaults for chart text. */
export const CHART_FONT = {
  body: FONT.body,
  mono: FONT.mono,
  axisSize: 11,
  legendSize: 12,
  tooltipSize: 12,
} as const;

/**
 * Resolve a token-driven color for a canonical theorem name.
 *
 * Falls back to the palette's primary color when the supplied name is
 * not in the registry. Returning the primary preserves the chart's
 * categorical encoding when a downstream caller supplies an aliased or
 * legacy theorem identifier.
 */
export function theoremSeriesColor(
  name: string,
  palette: ChartPalette = DEFAULT_CHART_PALETTE,
): string {
  if (name in THEOREM_COLOR) {
    return THEOREM_COLOR[name as CanonicalTheoremName];
  }
  return palette.primary;
}
